import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("auth.login_title"));
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-base px-6 relative">
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-brand-cyan/20 blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-brand-orange/20 blur-[120px]" />
      </div>

      <div className="absolute top-6 right-6"><LanguageSwitcher compact /></div>

      <div className="relative w-full max-w-md">
        <Link to="/" className="block text-center mb-8 font-display font-black text-3xl">
          <span className="text-gradient-vel7o">Vel7o</span><span className="text-white">.com</span>
        </Link>
        <div className="glass rounded-lg p-8" data-testid="login-card">
          <h1 className="font-display font-bold text-2xl mb-2">{t("auth.login_title")}</h1>
          <p className="text-white/60 text-sm mb-6">{t("auth.login_subtitle")}</p>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest font-bold text-white/70">{t("auth.email")}</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-black/50 border-white/10 focus:border-brand-cyan focus:ring-brand-cyan/30 text-white" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest font-bold text-white/70">{t("auth.password")}</Label>
              <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-black/50 border-white/10 focus:border-brand-cyan focus:ring-brand-cyan/30 text-white" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-brand-orange hover:bg-brand-orange-hover text-white font-medium">
              {loading ? "…" : t("common.login")}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-white/60">
            {t("auth.no_account")} <Link to="/register" data-testid="goto-register" className="text-brand-cyan hover:underline">{t("common.register")}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
